import { load, type ReCaptchaInstance } from 'recaptcha-v3';
import Bugsnag from '@bugsnag/js';
import { capturePostHogEvent } from '@/plugins/posthog';

const SITE_KEY = '6LeowvAdAAAAAFTChiD9pWNmWmt7uTyMRpncRGya';
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

/**
 * Shown when no token could be obtained at all - the script was blocked or
 * Google is unreachable (TPL-3489) - which is a network problem on the user's
 * side, not a failed check, so say what they can do about it.
 */
export const RECAPTCHA_UNAVAILABLE_MESSAGE =
    "Captcha couldn't load. Check your internet connection or turn off your ad blocker for this site, then try again.";

/** The same, short enough to sit under a form field. */
export const RECAPTCHA_UNAVAILABLE_FIELD_MESSAGE = "Captcha couldn't load.";

let instancePromise: Promise<ReCaptchaInstance> | null = null;

function loadInstance(): Promise<ReCaptchaInstance> {
    if (!instancePromise) {
        instancePromise = load(SITE_KEY, {
            autoHideBadge: true,
        }).catch((error) => {
            // Reset so next call can retry
            instancePromise = null;
            throw error;
        });
    }
    return instancePromise;
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * A blocked or failed `<script>` rejects with a DOM `Event`, not an `Error`.
 */
const errorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof Event !== 'undefined' && error instanceof Event) {
        return `script ${error.type}`;
    }

    return String(error);
};

export function useRecaptchaToken() {
    const getRecaptchaToken = async (action: string): Promise<string> => {
        let lastError = 'empty token';

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                const instance = await loadInstance();
                const token = await instance.execute(action);
                if (token) {
                    return token;
                }
                lastError = 'empty token';
            } catch (error) {
                lastError = errorMessage(error);
            }

            // Reset cached promise so next attempt tries fresh
            instancePromise = null;

            if (attempt < MAX_RETRIES) {
                await delay(RETRY_DELAY_MS);
            }
        }

        /**
         * Usually an ad blocker, privacy setting or network blocking Google on
         * the user's side. The caller shows "Captcha couldn't load" and sends
         * nothing, so this is not reported as a Bugsnag error (TPL-3720). A
         * PostHog event keeps the failure rate visible, so a site-wide outage
         * (revoked key, CSP change) still shows up as a spike.
         */
        const details = { action, attempts: MAX_RETRIES + 1, error: lastError };

        Bugsnag.leaveBreadcrumb('ReCaptcha token retrieval failed', details, 'error');
        capturePostHogEvent('recaptcha_unavailable', details);

        return '';
    };

    return { getRecaptchaToken };
}
